import React from 'react';
import { Sale, SaleItem, Payment, Customer, Plant } from '../../types';
import { Phone, MapPin, User, Mail } from 'lucide-react';
import { useContact } from '../../contexts/ContactContext';

interface ReceiptGeneratorProps {
  order: Sale;
  customer: Customer;
  payments?: Payment[];
  plantDetails?: Plant;
  receiptType: 'order' | 'payment' | 'completion';
  onClose: () => void;
  onPrint: () => void;
}

const ReceiptGenerator: React.FC<ReceiptGeneratorProps> = ({
  order,
  customer,
  payments = [],
  plantDetails,
  receiptType,
  onClose,
  onPrint
}) => {
  const { contactInfo } = useContact();
  const generateReceiptNumber = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `RCP-${timestamp}-${random}`;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Helper to get order items (handles legacy orders)
  const getOrderItemsForDisplay = (): SaleItem[] => {
    // If order has items array, return it
    if (order.items && order.items.length > 0) {
      return order.items;
    }

    // Convert legacy single-plant order to items array format
    if (order.plantId || order.plantName) {
      const legacyItem: SaleItem = {
        id: `legacy-${order.id}`,
        plantId: order.plantId || '',
        plantName: plantDetails?.name || order.plantName || '-',
        plantBrand: plantDetails?.brand || order.plantBrand,
        plantVariety: plantDetails?.variety || order.plantVariety,
        pricePerPlant: plantDetails?.pricePerPlant || (order as any).pricePerPlant || 0,
        quantity: order.quantity || order.totalTrays * ((order as any).seedsPerTray || plantDetails?.seedsPerTray || 126),
        totalPrice: order.totalAmount,
        seedsPerTray: (order as any).seedsPerTray || plantDetails?.seedsPerTray || 126,
        traysRequired: order.totalTrays,
      };
      return [legacyItem];
    }

    return [];
  };

  const getReceiptTitle = () => {
    switch (receiptType) {
      case 'order':
        return 'ORDER RECEIPT';
      case 'payment':
        return 'PAYMENT RECEIPT';
      case 'completion':
        return 'COMPLETION RECEIPT';
      default:
        return 'RECEIPT';
    }
  };

  const getStatusText = (status: any) => {
    return status?.name || 'Pending';
  };

  const receiptNumber = generateReceiptNumber();
  const currentDate = new Date().toLocaleDateString('en-IN');
  const currentTime = new Date().toLocaleTimeString('en-IN');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-2 sm:px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-11/12 sm:max-w-4xl sm:w-full">
          {/* Receipt Header */}
          <div className="bg-green-600 px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">{getReceiptTitle()}</h3>
              <div className="text-white text-sm">
                <p>Date: {currentDate}</p>
                <p>Time: {currentTime}</p>
              </div>
            </div>
          </div>

          {/* Receipt Content */}
          <div className="px-4 sm:px-6 py-4">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              {/* Receipt Header with Logo */}
              <div className="mb-6 border-b-2 border-green-600 pb-4">
                <div className="flex items-center justify-center gap-3">
                  <img
                    src={`${process.env.PUBLIC_URL}/nursery_logo.png`}
                    alt="Kariyas Nursery Logo"
                    className="h-12 w-12 object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div>
                    <h2 className="text-xl font-bold text-green-700 uppercase tracking-wide">Kariyas Nursery</h2>
                    <p className="text-xs text-gray-500">Premium & High Quality Plants</p>
                  </div>
                </div>
                <div className="text-center mt-3 p-2 bg-gray-100 rounded">
                  <p className="text-sm font-medium text-gray-700">Receipt No: {receiptNumber}</p>
                </div>
              </div>

              {/* Contact Details */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Kariyas Nursery</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        <span>{contactInfo.address}</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        <span>{contactInfo.primaryPhone} | {contactInfo.secondaryPhone}</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2" />
                        <span>{contactInfo.email}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Customer</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2" />
                        <span>{customer.name}</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        <span>{customer.phone}</span>
                      </div>
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mr-2 mt-0.5" />
                        <span>{customer.address}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p><span className="font-medium">Order ID:</span> #{order.orderId || order.id}</p>
                    <p><span className="font-medium">Order Date:</span> {formatDate(order.orderDate)}</p>
                    <p><span className="font-medium">Status:</span> {getStatusText(order.status)}</p>
                  </div>
                  <div>
                    <p><span className="font-medium">Expected Delivery:</span> {formatDate((order as any).expectedDeliveryDate || (order as any).deliveryDate)}</p>
                    <p><span className="font-medium">Total Trays:</span> {order.totalTrays}</p>
                    <p><span className="font-medium">Total Plants:</span> {(() => {
                      const items = getOrderItemsForDisplay();
                      return items.reduce((sum, item) => sum + item.quantity, 0).toLocaleString();
                    })()}</p>
                  </div>
                </div>

                {/* Product Details */}
                <div className="mt-4 bg-gray-50 rounded-lg p-3">
                  <h4 className="font-medium text-gray-800 mb-2">Order Items</h4>
                  {(() => {
                    const items = getOrderItemsForDisplay();
                    if (items.length === 1) {
                      // Single item display (legacy style)
                      const item = items[0];
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p><span className="font-medium">Plant Name:</span> {item.plantName}</p>
                            <p><span className="font-medium">Brand:</span> {item.plantBrand || '-'}</p>
                            <p><span className="font-medium">Variety:</span> {item.plantVariety || '-'}</p>
                          </div>
                          <div>
                            <p><span className="font-medium">Price:</span> {formatCurrency(item.pricePerPlant)} per plant</p>
                            <p><span className="font-medium">Quantity:</span> {item.quantity.toLocaleString()} plants</p>
                            <p><span className="font-medium">Total:</span> {formatCurrency(item.totalPrice)}</p>
                          </div>
                        </div>
                      );
                    } else {
                      // Multi-item table display
                      return (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-700">Plant</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">Qty</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">Rate</th>
                                <th className="px-3 py-2 text-right font-medium text-gray-700">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {items.map((item, index) => (
                                <tr key={item.id || index}>
                                  <td className="px-3 py-2">
                                    <div className="font-medium">{item.plantName}</div>
                                    {(item.plantBrand || item.plantVariety) && (
                                      <div className="text-xs text-gray-500">
                                        {item.plantBrand}{item.plantVariety ? ` - ${item.plantVariety}` : ''}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right">{item.quantity.toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right">{formatCurrency(item.pricePerPlant)}</td>
                                  <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.totalPrice)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-gray-100">
                              <tr>
                                <td className="px-3 py-2 font-semibold">Total</td>
                                <td className="px-3 py-2 text-right font-semibold">
                                  {items.reduce((sum, item) => sum + item.quantity, 0).toLocaleString()}
                                </td>
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2 text-right font-bold text-green-600">
                                  {formatCurrency(items.reduce((sum, item) => sum + item.totalPrice, 0))}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      );
                    }
                  })()}
                </div>
              </div>

              {/* Amount Details */}
              <div className="border-b border-gray-200 pb-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Amount Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Amount:</span>
                    <span className="font-medium">{formatCurrency(order.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Amount Paid:</span>
                    <span className="font-medium text-green-600">{formatCurrency(order.amountPaid)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Due Amount:</span>
                    <span className="font-medium text-red-600">{formatCurrency(order.remainingAmount)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold">
                      <span>Balance:</span>
                      <span className={order.remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}>
                        {order.remainingAmount > 0 ? `Due: ${formatCurrency(order.remainingAmount)}` : 'Paid in Full'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment History (if applicable) */}
              {payments.length > 0 && (
                <div className="border-b border-gray-200 pb-4 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment History</h3>
                  <div className="space-y-2">
                    {payments.map((payment, index) => (
                      <div key={payment.id} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">Payment {index + 1}</span>
                          <p className="text-gray-600">{formatDate(payment.paymentDate)}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">{formatCurrency(payment.amount)}</span>
                          <p className="text-gray-600">{payment.paymentMethod}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Receipt Footer */}
              <div className="text-center pt-4">
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Thank you for choosing Kariyas Nursery!
                  </p>
                  <p className="text-xs text-gray-500">
                    For any queries, please contact us at {contactInfo.primaryPhone} | {contactInfo.secondaryPhone}
                  </p>
                  <p className="text-xs text-gray-500">
                    {contactInfo.email}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Close
            </button>
            <button
              onClick={onPrint}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Print Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptGenerator; 